import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getAuthenticatedApiClient } from "../../../api/apiClient";
import type {
  ApproveCountryOnboardingRequest,
  CreateCountryRequest,
  RejectCountryOnboardingRequest
} from "../../../api/Api";
import { OnboardingStatusFilter } from "../../../components/onboardingRequests/types";
import { countryKeys } from "../../countries/dataHooks/countryQueries";

export const countryOnboardingRequestKeys = {
  all: ["countryOnboardingRequests"] as const,
  lists: () => [...countryOnboardingRequestKeys.all, "list"] as const,
  list: (status: OnboardingStatusFilter = "Pending") =>
    [...countryOnboardingRequestKeys.lists(), status] as const,
  mine: () => [...countryOnboardingRequestKeys.all, "mine"] as const
};

export const useCountryOnboardingRequests = (
  statusFilter: OnboardingStatusFilter = "Pending",
  options?: { refetchInterval?: number }
) => {
  return useQuery({
    queryKey: countryOnboardingRequestKeys.list(statusFilter),
    queryFn: async () => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.countryOnboardingRequests.list({
        status: statusFilter
      });
      return response.data;
    },
    refetchInterval: options?.refetchInterval
  });
};

export const useMyCountryOnboardingRequests = () => {
  return useQuery({
    queryKey: countryOnboardingRequestKeys.mine(),
    queryFn: async () => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.countryOnboardingRequests.mine();
      return response.data;
    }
  });
};

type MutationCallbacks = {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

export const useSubmitCountryOnboardingRequest = (
  options?: MutationCallbacks
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CreateCountryRequest) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.countryOnboardingRequests.submit(body);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: countryOnboardingRequestKeys.all
      });
      options?.onSuccess?.();
    },
    onError: (error) => options?.onError?.(error as Error)
  });
};

export const useApproveCountryOnboardingRequest = (
  options?: MutationCallbacks
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      id: string;
      body: ApproveCountryOnboardingRequest;
    }) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.countryOnboardingRequests.approve(
        vars.id,
        vars.body
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: countryOnboardingRequestKeys.all
      });
      queryClient.invalidateQueries({ queryKey: countryKeys.all });
      options?.onSuccess?.();
    },
    onError: (error) => options?.onError?.(error as Error)
  });
};

export const useRejectCountryOnboardingRequest = (
  options?: MutationCallbacks
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      id: string;
      body: RejectCountryOnboardingRequest;
    }) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.countryOnboardingRequests.reject(
        vars.id,
        vars.body
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: countryOnboardingRequestKeys.all
      });
      options?.onSuccess?.();
    },
    onError: (error) => options?.onError?.(error as Error)
  });
};
